import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { saveAs } from 'file-saver';
import { format, parseISO } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { FileSignature, Upload, Loader2, CheckCircle2, AlertCircle, Eraser, Eye, Download } from 'lucide-react';
import PdfViewer from './PdfViewer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => { try { return format(parseISO(iso), 'dd MMM yyyy HH:mm'); } catch { return iso || ''; } };

// Simple draw-with-finger/mouse signature pad.
function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    hasInk.current = true;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (hasInk.current) onChange(canvasRef.current.toDataURL('image/png'));
  };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    hasInk.current = false;
    onChange(null);
  };

  return (
    <div className="space-y-1.5">
      <canvas
        ref={canvasRef}
        width={560}
        height={160}
        className="w-full h-36 bg-white rounded-sm border-2 border-dashed border-primary/50 touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid="contract-signature-pad"
      />
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-muted-foreground">Draw your signature above with your finger or mouse</p>
        <Button type="button" size="sm" variant="ghost" onClick={clear} className="h-6 text-[11px]" data-testid="contract-signature-clear">
          <Eraser className="w-3 h-3 mr-1" /> Clear
        </Button>
      </div>
    </div>
  );
}

export default function EmploymentContract({ mode = 'employee', employeeId = null }) {
  const isAdmin = mode === 'admin';
  const [current, setCurrent] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [signName, setSignName] = useState('');
  const [signImage, setSignImage] = useState(null);
  const [agree, setAgree] = useState(false);
  const [signing, setSigning] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      if (isAdmin) {
        const r = await axios.get(`${API}/admin/employees/${employeeId}/contract`);
        setCurrent(r.data.current);
        setVersions(r.data.versions || []);
      } else {
        const r = await axios.get(`${API}/users/me/contract`);
        setCurrent(r.data.current);
      }
    } catch {
      /* silent — section simply shows empty state */
    } finally {
      setLoading(false);
    }
  }, [isAdmin, employeeId]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Contract must be a PDF file'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/admin/employees/${employeeId}/contract`, fd);
      toast.success('Contract uploaded — employee notified to sign');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const contractUrl = () => (isAdmin
    ? `${API}/admin/contracts/${current.id}/download`
    : `${API}/users/me/contract/${current.id}/download`);

  const downloadContract = async () => {
    try {
      const r = await axios.get(contractUrl(), { responseType: 'blob' });
      saveAs(new Blob([r.data], { type: 'application/pdf' }), current?.file_name || 'contract.pdf');
    } catch {
      toast.error('Could not download the contract');
    }
  };

  const openViewer = async () => {
    setViewOpen(true);
    setPdfUrl(null);
    try {
      const r = await axios.get(contractUrl(), { responseType: 'blob' });
      setPdfUrl(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })));
    } catch {
      toast.error('Could not load the contract');
    }
  };

  const handleSign = async () => {
    setSigning(true);
    try {
      const r = await axios.post(`${API}/users/me/contract/${current.id}/sign`, {
        signed_name: signName, signature_image: signImage, agree,
      });
      setCurrent(r.data);
      setViewOpen(false);
      toast.success('Contract signed — thank you!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not sign the contract');
    } finally {
      setSigning(false);
    }
  };

  if (loading) return null;
  if (!isAdmin && !current) return null; // employee with no contract → hide section

  const statusBadge = current ? (
    current.signed ? (
      <Badge className="bg-green-600 hover:bg-green-600 text-[10px]" data-testid="contract-status-signed">
        <CheckCircle2 className="w-3 h-3 mr-1" /> SIGNED {current.signed_at ? `· ${fmt(current.signed_at)}` : ''}
      </Badge>
    ) : (
      <Badge className="bg-orange-600 hover:bg-orange-600 text-[10px]" data-testid="contract-status-awaiting">
        <AlertCircle className="w-3 h-3 mr-1" /> AWAITING SIGNATURE
      </Badge>
    )
  ) : null;

  return (
    <Card className="bg-card border-border rounded-sm" data-testid="employment-contract-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm uppercase tracking-wider flex items-center gap-2 flex-wrap">
          <FileSignature className="w-4 h-4 text-primary" />
          Employment Contract
          {statusBadge}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!isAdmin && current && !current.signed && (
          <div className="rounded-sm border border-orange-500/40 bg-orange-500/10 p-3 text-xs text-orange-300" data-testid="contract-sign-banner">
            Your employment contract is awaiting your signature. Open it below, read it, then sign directly on screen.
          </div>
        )}

        {current ? (
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="text-foreground font-medium">{current.file_name}</span>
            <span>v{current.version}</span>
            <span>Uploaded {fmt(current.uploaded_at)}{current.uploaded_by_name ? ` by ${current.uploaded_by_name}` : ''}</span>
            <Button size="sm" variant="outline" onClick={openViewer} className="h-7 text-[11px] rounded-sm" data-testid="contract-view-btn">
              <Eye className="w-3 h-3 mr-1" /> {!isAdmin && !current.signed ? 'Review & Sign' : 'View Contract'}
            </Button>
            <Button size="sm" variant="outline" onClick={downloadContract} className="h-7 text-[11px] rounded-sm" data-testid="contract-download-btn">
              <Download className="w-3 h-3 mr-1" /> Download
            </Button>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">No employment contract uploaded yet.</p>
        )}

        {current?.signed && current.signature_image && (
          <div className="flex items-center gap-3 rounded-sm border border-green-500/30 bg-green-500/5 p-2.5">
            <img src={current.signature_image} alt="Signature" className="h-12 bg-white rounded-sm px-2" data-testid="contract-signature-img" />
            <div className="text-xs text-muted-foreground">
              Signed by <strong className="text-foreground">{current.signed_name}</strong>
              <br />{fmt(current.signed_at)} · contract v{current.version}
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files?.[0])}
              data-testid="contract-file-input"
            />
            <Button
              size="sm"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              className="rounded-sm h-8 bg-primary text-primary-foreground hover:bg-yellow-300"
              data-testid="contract-upload-btn"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
              {current ? 'Upload New Version (PDF)' : 'Upload Contract (PDF)'}
            </Button>
            {current && <p className="text-[10px] text-muted-foreground">A new version resets the signature — the employee must sign again.</p>}
          </div>
        )}

        {isAdmin && versions.length > 1 && (
          <div className="space-y-1">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Version history</p>
            {versions.filter((v) => !v.is_current_version).map((v) => (
              <div key={v.id} className="text-[11px] text-muted-foreground flex items-center gap-2" data-testid={`contract-version-${v.version}`}>
                <span>v{v.version} · {v.file_name} · {fmt(v.uploaded_at)}</span>
                {v.signed ? <span className="text-green-500">signed by {v.signed_name} · {fmt(v.signed_at)}</span> : <span className="text-orange-400">never signed</span>}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={viewOpen} onOpenChange={(o) => { setViewOpen(o); if (!o && pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); } }}>
        <DialogContent className="bg-card border-border rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Employment Contract {current ? `— v${current.version}` : ''}</DialogTitle>
            <DialogDescription>
              {!isAdmin && current && !current.signed
                ? 'Read the contract below, then sign at the bottom — no download needed.'
                : 'Contract preview'}
            </DialogDescription>
          </DialogHeader>
          {pdfUrl ? (
            <div className="space-y-2">
              <a
                href={pdfUrl}
                download={current?.file_name || 'contract.pdf'}
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground hover:text-primary"
                data-testid="contract-download-link"
              >
                <Download className="w-3 h-3" /> Download PDF (optional)
              </a>
              <div className="max-h-[55vh] overflow-y-auto rounded-sm border border-border p-1 bg-zinc-950">
                <PdfViewer url={pdfUrl} />
              </div>
            </div>
          ) : (
            <div className="h-[55vh] flex items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading contract…
            </div>
          )}

          {!isAdmin && current && !current.signed && (
            <div className="space-y-3 border-t border-border pt-4" data-testid="contract-sign-area">
              <p className="text-sm font-semibold text-foreground uppercase tracking-wider">Sign this contract</p>
              <SignaturePad onChange={setSignImage} />
              <Input
                value={signName}
                onChange={(e) => setSignName(e.target.value)}
                placeholder="Type your full legal name"
                className="rounded-sm"
                data-testid="contract-sign-name"
              />
              <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  className="mt-0.5"
                  data-testid="contract-sign-agree"
                />
                I confirm I have read, understood and agree to this employment contract.
              </label>
              <Button
                disabled={signing || !agree || !signImage || signName.trim().length < 3}
                onClick={handleSign}
                className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-yellow-300 uppercase tracking-wider"
                data-testid="contract-sign-btn"
              >
                {signing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing…</> : <><FileSignature className="w-4 h-4 mr-2" /> Sign Contract</>}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
